import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Collapse,
  FormControl,
  TextareaAutosize,
  Stack,
} from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { executeQueryApi } from "API/apis/reports/executeQuery";
import { useGlobalContext } from "context/GlobalContext";
import { STATUS_CODES } from "constants/responses/statusCodes";
import { message } from "constants/responses/message";
import { prohibitedQuery } from "./constants";
import { DataGridWithHighlightClass } from "components/DataGrid/DataGridWithHighlightClass";
import { CopyGridToClipboard } from "components/Export/CopyGridToClipboard";
import { getQueryListApi } from "API/apis/reports/getQueryList";
import { SingleSelect } from "components/Select/SingleSelect";
import { CopyGridToJsonClipboard } from "components/Export/CopyGridToJsonClipboard";

export const CustomReports = () => {
  const apiRef = useGridApiRef();
  const { pageAccess } = useGlobalContext();
  const [expanded, setExpanded] = useState(false);
  const [sqlQuery, setSqlQuery] = useState("");
  const { setIsLoading, setError, setSuccess } = useGlobalContext();
  const [records, setRecords] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [queryList, setQueryList] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState();

  useEffect(() => {
    const fetchList = async () => {
      const { data, status } = await getQueryListApi();
      if (status !== STATUS_CODES.SUCCESS) {
        throw new Error(data.message);
      }
      setQueryList(
        data.map((item) => ({ option: item.query, value: item.name }))
      );
    };
    fetchList();
  }, []);

  useEffect(() => {
    setSqlQuery(selectedQuery);
  }, [selectedQuery]);

  const executeQuery = async () => {
    if (!sqlQuery) return;
    const upperQuery = sqlQuery.trim().toUpperCase();
    setIsLoading(true);
    if (prohibitedQuery.some((prefix) => upperQuery.startsWith(prefix))) {
      setError(message.error.actionNotAllowed);
      return;
    }
    try {
      const { data, status } = await executeQueryApi(sqlQuery);
      if (status !== STATUS_CODES.SUCCESS) {
        throw new Error(data.message);
      }
      setGridColumns(
        records.length > 0
          ? Object.keys(records[0]).map((item) => ({
              field: item,
              headerName: item,
              flex: 1,
            }))
          : []
      );
      setRecords(data);
      setSuccess(message.success.generalSuccessFetchMessage);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!records.length) return;
    const worksheet = XLSX.utils.json_to_sheet(records);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, "report.xlsx");
  };

  return (
    <Box p={2}>
      {/* Dropdown */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <SingleSelect
          label="Select Query"
          name="selectQuery"
          handleSelectSingle={(e) => setSelectedQuery(e.target.value)}
          options={queryList}
          selectedOption={selectedQuery}
        />
      </FormControl>

      {/* Header and Toggle Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Description</Typography>
        <Box>
          <Button variant="outlined" onClick={executeQuery}>
            Fetch
          </Button>
          <Button variant="outlined" onClick={() => setExpanded(!expanded)}>
            {expanded ? "Collapse" : "Expand"}
          </Button>
        </Box>
      </Box>

      {/* Collapsible TextArea Input */}
      <Collapse in={expanded} collapsedSize={100}>
        <Box
          mt={1}
          p={2}
          sx={{
            border: "1px solid #ccc",
            borderRadius: 2,
            height: expanded ? "50vh" : "100px",
            overflowY: "auto",
            transition: "height 0.3s ease",
            backgroundColor: "#fff",
          }}
        >
          <TextareaAutosize
            minRows={5}
            style={{
              width: "100%",
              resize: "none",
              fontSize: "1rem",
              padding: "8px",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
            }}
            placeholder="Enter description here..."
            value={sqlQuery}
            disabled={!pageAccess.canAdmin}
            onChange={(e) => setSqlQuery(e.target.value)}
          />
        </Box>
      </Collapse>

      {/* Table Section */}
      <Box mt={2} width="100%">
        <DataGridWithHighlightClass
          getRowId={(option) => option}
          apiRef={apiRef}
          rows={records}
          columns={gridColumns}
          autoHeight
          disableRowSelectionOnClick
        />
      </Box>

      {/* Buttons Below Table */}
      <Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
        <CopyGridToClipboard rows={records} columns={gridColumns} />
        <CopyGridToJsonClipboard rows={records} />
        <Button variant="contained" onClick={handleDownloadExcel}>
          Download as Excel
        </Button>
      </Stack>
    </Box>
  );
};
